import MiniSearch from 'minisearch';
import {searchIndexUtils} from './song-search-index-utils';
import {prepareSearchDocument, buildSearchGroups, buildSearchCorpus, rankSongGroups} from './song-search-model';
import {normalizeSearchText} from './song-search-text';

const emptyState = () => ({status: 'missing', progress: 0, fileCount: 0, revision: 0, error: null});
const nextTask = () => new Promise(resolve => setTimeout(resolve, 0));

// Each workspace owns its cache, in-flight job and published snapshot.
export class SongSearchService {
  constructor({readFiles, readContent, onRebuilt = () => {}, onWorkspace = () => {}}) {
    this.readFiles = readFiles;
    this.readContent = readContent;
    this.onRebuilt = onRebuilt;
    this.onWorkspace = onWorkspace;
    this.workspaces = new Map();
    this.listeners = new Set();
  }

  workspace(id) {
    if (!this.workspaces.has(id)) this.workspaces.set(id, {cache: new Map(), documents: [], groups: [],
      corpus: buildSearchCorpus([]), state: emptyState(), version: 0, job: null, queued: false, miniSearch: null});
    return this.workspaces.get(id);
  }

  subscribe(listener) { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  notify(id, patch) {
    const workspace = this.workspace(id);
    workspace.state = {...workspace.state, ...patch};
    this.listeners.forEach(listener => listener(id));
  }
  state(id) { return this.workspace(id).state; }

  markDirty(id) {
    const ws = this.workspace(id);
    ws.version += 1;
    if (ws.state.status !== 'indexing') this.notify(id, {status: ws.miniSearch ? 'dirty' : 'missing'});
    // No rebuild while typing: changes only mark the snapshot stale.
  }

  rebuild(id) {
    const ws = this.workspace(id);
    if (ws.job) { ws.queued = true; return ws.job; }
    ws.job = this.build(id, ws).finally(async () => {
      ws.job = null;
      if (ws.queued) { ws.queued = false; await this.rebuild(id); }
    });
    return ws.job;
  }

  async build(id, ws) {
    const version = ws.version;
    this.notify(id, {status: 'indexing', progress: 0, error: null});
    try {
      const names = (await this.readFiles(id)).slice().sort();
      this.onWorkspace(id, names);
      const nextCache = new Map();
      const documents = [];
      const miniSearch = new MiniSearch({idField: 'id', fields: ['title', 'lyrics', 'metadata', 'fileName'],
        processTerm: normalizeSearchText});
      let sliceStart = performance.now();
      for (const [index, name] of names.entries()) {
        const content = await this.readContent(id, name);
        if (content === null || content === undefined) throw new Error(`Nie można odczytać ${name}`);
        const previous = ws.cache.get(name);
        const entry = previous?.content === content ? previous : {content,
          documents: (await searchIndexUtils.explodeSongs(name, content)).map(prepareSearchDocument)};
        nextCache.set(name, entry);
        documents.push(...entry.documents);
        miniSearch.addAll(entry.documents);
        this.notify(id, {progress: Math.round((index + 1) / Math.max(1, names.length) * 85), fileCount: names.length});
        if (performance.now() - sliceStart >= 6) { await nextTask(); sliceStart = performance.now(); }
      }
      this.notify(id, {progress: 90});
      const groups = buildSearchGroups(documents);
      const corpus = buildSearchCorpus(documents);
      // Publish atomically; a failure keeps the previous searchable snapshot.
      Object.assign(ws, {cache: nextCache, documents, groups, corpus, miniSearch});
      this.notify(id, {status: ws.version === version ? 'ready' : 'dirty', progress: 100,
        fileCount: names.length, revision: ws.state.revision + 1});
      this.onRebuilt(id, names.length);
    } catch (error) {
      this.notify(id, {status: 'error', error: error.message});
    }
  }

  search(id, query) {
    const ws = this.workspace(id);
    if (!query.trim() || !ws.miniSearch) return [];
    // All-term prefix/fuzzy fallback is used only if literal matching finds nothing.
    const fallbackHits = ws.miniSearch.search(query, {combineWith: 'AND', prefix: true, fuzzy: 0.2,
      boost: {title: 2}});
    return rankSongGroups({query, ...ws, fallbackHits});
  }
}
